// Datos de Inteligencia › Maquinaria, Entradas y salidas, Materiales y Acopio.
// Salen de las fotos que ya analizó el modelo nuevo (iaV3: RF-DETR, Apache-2.0) y que guarda el servicio de
// entregas en Firestore (iaV3/{feedId}/{yyyy}/{mm}/day). El backend las sirve en /api/entregas/:feedId/fotos.
// Todo lo demás (búsqueda, vehículos, materiales nuevos, acopio) se calcula en el navegador: cero costo extra de IA.
// Personas sale del YOLOX de siempre (pirIaAnalyzes, mismo endpoint que el Dashboard IA).
import axios from 'axios';
import { dayKey, arHour, loadDays, listDays } from '../iaData';

const BASE = process.env.REACT_APP_ENTREGAS_API_URL || process.env.REACT_APP_BACKEND_URL || 'http://localhost:8081';
const api = axios.create({ baseURL: `${BASE.replace(/\/$/, '')}/api/entregas`, timeout: 20000 });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authLisualToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ---------------------------------------------------------------------------
// Categorías del buscador (como el selector de objetos de Evercam). `cls` = clases del modelo iaV3.
// Las que el modelo todavía no reconoce quedan visibles pero apagadas ("Próximamente"): se dice, no se inventa.
// ---------------------------------------------------------------------------
export const CATEGORIAS = [
  { key: 'volquete', label: 'Camión volquete', icon: '🚛', grupo: 'Vehículos', cls: ['volquete'], color: '#f97316' },
  { key: 'camion', label: 'Camión', icon: '🚚', grupo: 'Vehículos', cls: ['camion'], color: '#c2410c' },
  { key: 'mixer', label: 'Mixer (hormigonera)', icon: '🌀', grupo: 'Vehículos', cls: ['mixer'], color: '#a855f7' },
  { key: 'bomba', label: 'Bomba de hormigón', icon: '🏗️', grupo: 'Vehículos', cls: ['bomba_hormigon'], color: '#7c3aed' },
  { key: 'camioneta', label: 'Camioneta', icon: '🚙', grupo: 'Vehículos', cls: ['camioneta'], color: '#6366f1' },
  { key: 'auto', label: 'Auto', icon: '🚗', grupo: 'Vehículos', cls: ['auto'], color: '#3b82f6' },
  { key: 'excavadora', label: 'Excavadora', icon: '⛏️', grupo: 'Máquinas', cls: ['excavadora'], color: '#f59e0b' },
  { key: 'retro', label: 'Retroexcavadora', icon: '🚜', grupo: 'Máquinas', cls: ['retroexcavadora'], color: '#d97706' },
  { key: 'minicargadora', label: 'Minicargadora', icon: '🚜', grupo: 'Máquinas', cls: ['minicargadora'], color: '#ca8a04' },
  { key: 'grua_movil', label: 'Grúa móvil', icon: '🏗️', grupo: 'Máquinas', cls: ['grua_movil'], color: '#b45309' },
  { key: 'autoelevador', label: 'Autoelevador', icon: '🏭', grupo: 'Máquinas', cls: ['autoelevador'], color: '#92400e' },
  { key: 'grua_torre', label: 'Grúa torre', icon: '🏗️', grupo: 'Máquinas', cls: [], color: '#9ca3af', proximamente: true },
  { key: 'persona', label: 'Persona', icon: '👷', grupo: 'Personas', cls: [], yolox: ['Persona con casco', 'Persona sin casco'], color: '#10b981' },
  { key: 'contenedor', label: 'Contenedor / obrador', icon: '📦', grupo: 'Obra', cls: ['contenedor_obrador'], color: '#0ea5e9' },
  // Tanques / cisternas parados que el modelo confunde con un mixer (regla de objetos fijos, ver reetiquetarFijos)
  { key: 'tanque', label: 'Tanque / cisterna', icon: '🛢️', grupo: 'Obra', cls: ['tanque_cisterna'], color: '#0891b2' },
  { key: 'pallet', label: 'Pallets (ladrillos, bloques)', icon: '🧱', grupo: 'Materiales', cls: ['pallet_material'], color: '#dc2626' },
  { key: 'bolson', label: 'Bolsones / bolsas', icon: '🛍️', grupo: 'Materiales', cls: ['bolson_bolsas'], color: '#e11d48' },
  { key: 'arena', label: 'Arena / tierra', icon: '⛰️', grupo: 'Materiales', cls: ['pila_arena_tierra'], color: '#a16207' },
  { key: 'piedra', label: 'Piedra / grava', icon: '🥌', grupo: 'Materiales', cls: ['pila_grava'], color: '#78716c' },
  { key: 'hierros', label: 'Hierros / mallas', icon: '🔩', grupo: 'Materiales', cls: ['hierros_malla'], color: '#475569' },
  { key: 'madera', label: 'Maderas / tablas', icon: '🌲', grupo: 'Materiales', cls: ['madera_tablas'], color: '#92400e' },
  { key: 'perfiles', label: 'Caños / perfiles', icon: '📏', grupo: 'Materiales', cls: ['perfil_cano_metal'], color: '#64748b' },
];
export const catInfo = (k) => CATEGORIAS.find((c) => c.key === k);
const CLS_CAT = new Map(CATEGORIAS.flatMap((c) => c.cls.map((x) => [x, c])));
export const catDeClase = (cls) => CLS_CAT.get(cls) || null;
export const colorClase = (cls) => CLS_CAT.get(cls)?.color || '#6b7280';
export const nombreClase = (cls) => CLS_CAT.get(cls)?.label || cls;

// Materiales que se pueden acopiar en obra (Materiales y Acopio)
export const MATERIALES = ['pallet_material', 'bolson_bolsas', 'pila_arena_tierra', 'pila_grava', 'hierros_malla', 'madera_tablas', 'perfil_cano_metal'];
export const MAT = {
  pallet_material: { label: 'Pallets (ladrillos, bloques)', corto: 'pallets', uno: 'pallet', icon: '🧱', color: '#dc2626', pila: false },
  bolson_bolsas: { label: 'Bolsones / bolsas', corto: 'bolsones', uno: 'bolsón', icon: '🛍️', color: '#e11d48', pila: false },
  pila_arena_tierra: { label: 'Pilas de arena / tierra', corto: 'pilas de arena', uno: 'pila de arena', icon: '⛰️', color: '#a16207', fem: true, pila: true },
  pila_grava: { label: 'Pilas de piedra / grava', corto: 'pilas de piedra', uno: 'pila de piedra', icon: '🥌', color: '#78716c', fem: true, pila: true },
  hierros_malla: { label: 'Hierros / mallas', corto: 'atados de hierro', uno: 'atado de hierro', icon: '🔩', color: '#475569', pila: false },
  madera_tablas: { label: 'Maderas / tablas', corto: 'pilas de madera', uno: 'pila de madera', icon: '🌲', color: '#92400e', fem: true, pila: false },
  perfil_cano_metal: { label: 'Caños / perfiles', corto: 'caños o perfiles', uno: 'caño o perfil', icon: '📏', color: '#64748b', pila: false },
};

// Vehículos y máquinas que entran y salen (Entradas y salidas)
export const VEHICULOS = ['auto', 'camioneta', 'camion', 'volquete', 'mixer', 'bomba_hormigon', 'excavadora', 'retroexcavadora', 'minicargadora', 'grua_movil', 'autoelevador'];

// ---------------------------------------------------------------------------
// Lectura
// ---------------------------------------------------------------------------
// Foto iaV3 -> misma forma que las fotos de iaData (ms, s3, w, h, dets con cajas en píxeles), así se reusan
// Photo, Visor y el seguimiento de vehículos del Calendario.
const normalizar = (f) => {
  const w = f.w || 1920, h = f.h || 1080;
  return {
    id: f.id || f.key,
    ms: f.ts,
    s3: f.key,
    w, h,
    hasBoxes: true,
    v3: true,
    dets: (f.detections || []).filter((d) => Array.isArray(d.box)).map((d) => ({
      cls: d.cls, conf: d.conf, area: d.area ?? (d.box[2] - d.box[0]) * (d.box[3] - d.box[1]),
      ...(d.cls_modelo ? { clsModelo: d.cls_modelo } : {}), ...(d.acopio !== undefined ? { acopio: d.acopio } : {}),
      x1: d.box[0] * w, y1: d.box[1] * h, x2: d.box[2] * w, y2: d.box[3] * h,
    })),
  };
};

// ---------------------------------------------------------------------------
// Objetos fijos mal reconocidos (misma regla que entregas-patch/v3/servicio/eventos_v3.py › reetiquetar_fijos):
// un "mixer" en el MISMO lugar en 2+ días distintos, o más de 3 h en un día, es un tanque / cisterna parado
// (OMI: cisterna blanca detrás de las excavadoras), no un mixer. Igual la bomba de hormigón (solo la regla de días).
// Solo grupos que el modelo ve con poca seguridad (mediana < 0,70): los mixers reales que descargan siempre en el
// mismo lugar salen con 0,8-0,95. Si en los días del medio el lugar queda vacío, es un vehículo que va y viene.
// La clase original queda en `clsModelo`.
// ---------------------------------------------------------------------------
const FIJOS = { mixer: 'tanque_cisterna', bomba_hormigon: 'objeto_fijo' };
const iouN = (a, b) => {
  const x1 = Math.max(a[0], b[0]), y1 = Math.max(a[1], b[1]), x2 = Math.min(a[2], b[2]), y2 = Math.min(a[3], b[3]);
  const i = Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
  return i / ((a[2] - a[0]) * (a[3] - a[1]) + (b[2] - b[0]) * (b[3] - b[1]) - i + 1e-9);
};
const bn = (p, d) => [d.x1 / p.w, d.y1 / p.h, d.x2 / p.w, d.y2 / p.h];
export function reetiquetarFijos(fotos) {
  const dk = (ms) => new Date(ms - 3 * 3600e3).toISOString().slice(0, 10);
  const porDiaF = new Map();
  for (const p of fotos) { const k = dk(p.ms); if (!porDiaF.has(k)) porDiaF.set(k, []); porDiaF.get(k).push(p); }
  const vaYViene = (box, d0, d1) => {
    let con = 0, vacios = 0;
    for (const [k, fs] of porDiaF) {
      if (k < d0 || k > d1 || fs.length < 8) continue;
      con += 1;
      if (!fs.some((p) => p.dets.some((d) => d.conf >= 0.35 && iouN(box, bn(p, d)) >= 0.3))) vacios += 1;
    }
    return con >= 3 && vacios >= con / 2;
  };
  for (const [cls, nueva] of Object.entries(FIJOS)) {
    const grupos = [];
    for (const p of fotos) {
      for (const d of p.dets) {
        if (d.cls !== cls || d.conf < 0.35) continue;
        const b = bn(p, d), a = (b[2] - b[0]) * (b[3] - b[1]);
        let mejor = null, mv = 0;
        for (const g of grupos) { const v = iouN(g.box, b); if (v > mv) { mv = v; mejor = g; } }
        const ag = mejor ? (mejor.box[2] - mejor.box[0]) * (mejor.box[3] - mejor.box[1]) : 1;
        if (mejor && mv >= 0.5 && a / ag >= 0.5 && a / ag <= 2) mejor.xs.push({ dia: dk(p.ms), ms: p.ms, d });
        else grupos.push({ box: b, xs: [{ dia: dk(p.ms), ms: p.ms, d }] });
      }
    }
    for (const g of grupos) {
      const confs = g.xs.map((x) => x.d.conf).sort((a, b) => a - b);
      if (confs[Math.floor(confs.length / 2)] >= 0.7) continue;
      const dias = new Map();
      for (const x of g.xs) { const r = dias.get(x.dia) || [x.ms, x.ms]; dias.set(x.dia, [Math.min(r[0], x.ms), Math.max(r[1], x.ms)]); }
      const ks = [...dias.keys()].sort();
      const fijo = ks.length >= 2 && !vaYViene(g.box, ks[0], ks[ks.length - 1]);
      const largo = cls === 'mixer' && [...dias.values()].some(([a, b]) => b - a > 3 * 3600e3);
      if (fijo || largo) for (const x of g.xs) { x.d.clsModelo = cls; x.d.cls = nueva; }
    }
  }
  return fotos;
}

// ---------------------------------------------------------------------------
// Correcciones del cliente en Maquinaria ("No es esto"): se guardan con el mismo mecanismo que el feedback de
// entregas (PUT /api/entregas/:feedId/feedback/:key -> entregasFeedback en Firestore; en local iav3.feedback.json),
// con la caja, la foto y la clase en `nota` (JSON) para reentrenar. Además quedan en el navegador para ocultarlas.
// ---------------------------------------------------------------------------
const LS = (feedId) => `lisual.inteligencia.correcciones.${feedId}`;
export const claveCorreccion = (p, d) => {
  const hm = new Date(p.ms - 3 * 3600e3).toISOString().slice(11, 16).replace(':', '');
  const h = Math.abs([...`${p.id}|${Math.round(d.x1)}|${Math.round(d.y1)}`].reduce((a, c) => (a * 31 + c.charCodeAt(0)) | 0, 7)).toString(36).slice(0, 6);
  return `${new Date(p.ms - 3 * 3600e3).toISOString().slice(0, 10)}_det-${hm}-${String(d.cls).slice(0, 16)}-${h}`;
};
export function leerCorrecciones(feedId) {
  try { return JSON.parse(localStorage.getItem(LS(feedId)) || '{}'); } catch (e) { return {}; }
}
export async function guardarCorreccion(feedId, p, d, { borrar = false } = {}) {
  const key = claveCorreccion(p, d);
  const doc = { t: 'deteccion', estado: 'no_es', cls: d.cls, clsModelo: d.clsModelo || d.cls, foto: p.id, s3: p.s3, w: p.w, h: p.h,
    box: bn(p, d).map((v) => Math.round(v * 1e4) / 1e4), conf: d.conf };
  const todas = leerCorrecciones(feedId);
  if (borrar) delete todas[key]; else todas[key] = { ...doc, at: new Date().toISOString() };
  try { localStorage.setItem(LS(feedId), JSON.stringify(todas)); } catch (e) { /* sin almacenamiento */ }
  const nota = JSON.stringify({ t: 'det', cls: doc.clsModelo, foto: doc.foto, box: doc.box, conf: doc.conf });
  try { await api.put(`/${encodeURIComponent(feedId)}/feedback/${encodeURIComponent(key)}`, { estado: borrar ? null : 'no', nota: nota.slice(0, 300) }); } catch (e) { /* queda en el navegador */ }
  return todas;
}

const MAX_TRAMO = 31; // días por pedido (tope del backend)
const sumarDias = (k, n) => new Date(Date.parse(k + 'T12:00:00Z') + n * 864e5).toISOString().slice(0, 10);
function tramos(desde, hasta) {
  const out = [];
  for (let d = desde; d <= hasta; d = sumarDias(d, MAX_TRAMO)) { const h = sumarDias(d, MAX_TRAMO - 1); out.push([d, h < hasta ? h : hasta]); }
  return out.length ? out : [[desde, hasta]];
}

const cache = new Map();
// { fuente: 'iaV3'|'ninguna', fotos: [...], analizadoDesde, ultimo, error? } — nunca tira error
export function cargarFotosV3(feedId, desde, hasta) {
  if (!feedId) return Promise.resolve({ fuente: 'ninguna', fotos: [] });
  const ck = `${feedId}|${desde}|${hasta}`;
  if (!cache.has(ck)) {
    // El backend da como mucho 31 días por pedido: un rango más largo se pide en tramos y se junta.
    const p = Promise.all(tramos(desde, hasta).map(([d, h]) => api.get(`/${encodeURIComponent(feedId)}/fotos`, { params: { desde: d, hasta: h } }).then((r) => r.data)))
      .then((partes) => {
        const vistas = new Set(), fotos = [];
        for (const data of partes) for (const f of data.fotos || []) { const id = f.id || f.key; if (!vistas.has(id)) { vistas.add(id); fotos.push(normalizar(f)); } }
        reetiquetarFijos(fotos);
        const x = partes[partes.length - 1] || {};
        return { fuente: partes.some((d) => d.fuente === 'iaV3') ? 'iaV3' : x.fuente, analizadoDesde: x.analizadoDesde || null, ultimo: x.ultimo || null, desde, hasta, fotos: fotos.sort((a, b) => a.ms - b.ms) };
      })
      .catch(() => { cache.delete(ck); return { fuente: 'ninguna', fotos: [], error: true }; });
    cache.set(ck, p);
    setTimeout(() => cache.delete(ck), 5 * 60e3);
  }
  return cache.get(ck);
}

// Personas: detecciones del YOLOX de producción (pirIaAnalyzes), las mismas del Dashboard IA
export async function cargarPersonas(cam, customerId, desde, hasta) {
  if (!cam || !customerId) return [];
  try {
    const fotos = await loadDays(cam, customerId, listDays(desde, hasta), null, { zonas: null });
    return fotos.filter((p) => p.hasBoxes).map((p) => ({ ...p, dets: p.dets.filter((d) => d.cls === 'Persona con casco' || d.cls === 'Persona sin casco') }));
  } catch (e) { return []; }
}

// ---------------------------------------------------------------------------
// Utilidades
// ---------------------------------------------------------------------------
export const enJornada = (ms, desde = 6, hasta = 20) => { const h = arHour(ms); return h >= desde && h < hasta; };
export const porDia = (fotos) => {
  const m = new Map();
  for (const p of fotos) { const k = dayKey(p.ms); if (!m.has(k)) m.set(k, []); m.get(k).push(p); }
  return m;
};

// Recorte alrededor de una caja (en píxeles de la foto), con la proporción pedida
export function recorte(p, b, { k = 2.2, ratio = 4 / 3, min = 0.12 } = {}) {
  const cx = (b.x1 + b.x2) / 2, cy = (b.y1 + b.y2) / 2;
  let w = Math.max((b.x2 - b.x1) * k, (b.y2 - b.y1) * k * ratio, p.w * min), h = w / ratio;
  if (w > p.w) { w = p.w; h = w / ratio; }
  if (h > p.h) { h = p.h; w = h * ratio; }
  const x1 = Math.max(0, Math.min(p.w - w, cx - w / 2)), y1 = Math.max(0, Math.min(p.h - h, cy - h / 2));
  return { x1, y1, x2: x1 + w, y2: y1 + h };
}

// Rango por defecto: la última semana con fotos analizadas (si la cámara recién empezó, desde el principio)
export const rangoDefecto = (hoy, dias = 7) => {
  const d = new Date(Date.parse(hoy + 'T12:00:00Z') - (dias - 1) * 864e5).toISOString().slice(0, 10);
  return { desde: d, hasta: hoy };
};

export const plural = (n, uno, varios) => `${String(n).replace('.', ',')} ${n === 1 ? uno : varios}`;
export const pct = (x, dec = 1) => `${(x * 100).toFixed(dec).replace('.', ',')} %`;
